import {
  BaseService,
  type BaseServiceContext,
  throwAppError,
} from '@bangle.io/base-utils';
import {
  FILE_STORAGE_MAX_FILE_SIZE_BYTES,
  SERVICE_NAME,
  WORKSPACE_STORAGE_TYPE,
} from '@bangle.io/constants';
import {
  backfillMissingFileContentStats,
  clearLocalFileYdoc,
  deleteFileYdoc,
  deleteWorkspaceFile,
  downloadAsset,
  getWorkspaceFile,
  insertWorkspaceFile,
  isCollabActive,
  listShareLinks,
  listWorkspaceFiles,
  refreshShareLink,
  renameFileYdoc,
  renameWorkspaceFile,
  SaveWorkspaceRpcMissingError,
  saveWorkspaceFileContent,
  subscribeWorkspaceFiles,
  updateWorkspaceFileContent,
  uploadAsset,
} from '@bangle.io/supabase';
import type {
  BaseFileStorageProvider,
  FileStat,
  FileStorageChangeEvent,
  FileStorageExternalChangeEvent,
  ListedNoteContentStat,
  ListedNoteMetadata,
} from '@bangle.io/types';
import { WsPath } from '@bangle.io/ws-path';
import {
  cacheWorkspaceFile,
  deleteCachedWorkspaceFile,
  getCachedWorkspaceFile,
  listCachedWorkspaceFiles,
  renameCachedWorkspaceFile,
} from './file-storage-supabase-cache';
import { assertSameWorkspaceRename } from './file-storage-utils';

type Config = {
  onChange: (event: FileStorageChangeEvent) => void;
  onExternalChange?: (event: FileStorageExternalChangeEvent) => void;
  getWorkspaceUuid: (wsName: string) => Promise<string>;
};

type WorkspaceFileMeta = {
  ctime: number;
  mtime: number;
  contentStat: ListedNoteContentStat | null;
};

const TEXT_LIKE = new Set([
  'text/markdown',
  'text/plain',
  'application/json',
  '',
]);

function relativePathFromWsPath(wsPath: string): string {
  const parsed = WsPath.fromString(wsPath);
  return parsed.path;
}

async function fileToContent(file: File): Promise<{
  content: string | null;
  storage_path: string | null;
  content_type: string;
  byte_size: number;
}> {
  const contentType = file.type || 'application/octet-stream';
  const isText =
    TEXT_LIKE.has(contentType) ||
    file.name.endsWith('.md') ||
    file.name.endsWith('.markdown') ||
    file.name.endsWith('.txt');

  if (isText) {
    const content = await file.text();
    return {
      content,
      storage_path: null,
      content_type: contentType.includes('markdown')
        ? 'text/markdown'
        : contentType || 'text/markdown',
      byte_size: new TextEncoder().encode(content).byteLength,
    };
  }

  return {
    content: null,
    storage_path: null,
    content_type: contentType,
    byte_size: file.size,
  };
}

export class FileStorageSupabase
  extends BaseService
  implements BaseFileStorageProvider
{
  public readonly workspaceType = WORKSPACE_STORAGE_TYPE.Supabase;
  public readonly maxFileSizeBytes = FILE_STORAGE_MAX_FILE_SIZE_BYTES.supabase;

  private onChange: (event: FileStorageChangeEvent) => void;
  private onExternalChange?: (event: FileStorageExternalChangeEvent) => void;
  private getWorkspaceUuid: (wsName: string) => Promise<string>;
  private subscriptions = new Map<string, () => void>();
  private localWritePaths = new Set<string>();
  /** Workspaces that already kicked a background stats backfill this session. */
  private contentStatsBackfillStarted = new Set<string>();
  /** path (workspace-relative) → meta from the last list / write. */
  private listedMetaByWorkspace = new Map<
    string,
    Map<string, WorkspaceFileMeta>
  >();
  private wsNameToId = new Map<string, string>();

  constructor(context: BaseServiceContext, dependencies: null, config: Config) {
    super(SERVICE_NAME.fileStorageSupabaseService, context, dependencies);
    this.onChange = config.onChange;
    this.onExternalChange = config.onExternalChange;
    this.getWorkspaceUuid = config.getWorkspaceUuid;
  }

  async hookMount(): Promise<void> {}

  private emitChange(event: FileStorageChangeEvent) {
    this.onChange(event);
  }

  private workspaceMeta(workspaceId: string): Map<string, WorkspaceFileMeta> {
    let map = this.listedMetaByWorkspace.get(workspaceId);
    if (!map) {
      map = new Map();
      this.listedMetaByWorkspace.set(workspaceId, map);
    }
    return map;
  }

  private setListedMeta(
    workspaceId: string,
    path: string,
    meta: WorkspaceFileMeta,
  ) {
    this.workspaceMeta(workspaceId).set(path, meta);
  }

  private contentStatFromRow(row: {
    word_count: number | null;
    char_count: number | null;
    char_count_no_spaces: number | null;
  }): ListedNoteContentStat | null {
    if (
      typeof row.word_count !== 'number' ||
      typeof row.char_count !== 'number' ||
      typeof row.char_count_no_spaces !== 'number'
    ) {
      return null;
    }
    return {
      words: row.word_count,
      chars: row.char_count,
      charsNoSpaces: row.char_count_no_spaces,
    };
  }

  getListedNoteMetadata(wsName: string): ListedNoteMetadata | null {
    // Resolve by scanning known workspace ids keyed via last list; callers
    // pass wsName so look up via getWorkspaceUuid is async — instead match
    // by reconstructing from the last list keys stored under workspaceId.
    // We store under workspaceId; FileSystemService calls this after list
    // with the same wsName, so we keep a name→id side map.
    const workspaceId = this.wsNameToId.get(wsName);
    if (!workspaceId) return null;
    const meta = this.listedMetaByWorkspace.get(workspaceId);
    if (!meta || meta.size === 0) return null;
    const fileStats = new Map<string, FileStat>();
    const contentStats = new Map<string, ListedNoteContentStat>();
    for (const [path, entry] of meta) {
      const wsPath = `${wsName}:${path}`;
      fileStats.set(wsPath, { ctime: entry.ctime, mtime: entry.mtime });
      if (entry.contentStat) {
        contentStats.set(wsPath, entry.contentStat);
      }
    }
    return { fileStats, contentStats };
  }

  private async ensureRealtime(wsName: string, workspaceId: string) {
    if (this.subscriptions.has(workspaceId)) return;
    const unsub = subscribeWorkspaceFiles(workspaceId, (payload) => {
      if (!payload.path) {
        this.onExternalChange?.({ type: 'refresh', wsName });
        return;
      }
      const key = `${wsName}:${payload.path}`;
      if (this.localWritePaths.has(key)) {
        return;
      }
      if (isCollabActive(workspaceId, payload.path)) {
        // Yjs is source of truth while collab is connected; skip LWW reload.
        return;
      }
      // INSERT and DELETE change the workspace tree; content UPDATE alone does
      // not. Without a refresh on INSERT, collaborators never see new notes
      // until a full page reload (or some other force-update).
      if (payload.eventType === 'DELETE' || payload.eventType === 'INSERT') {
        this.onExternalChange?.({
          type: 'update',
          wsPath: `${wsName}:${payload.path}`,
        });
        this.onExternalChange?.({ type: 'refresh', wsName });
        return;
      }
      this.onExternalChange?.({
        type: 'update',
        wsPath: `${wsName}:${payload.path}`,
      });
    });
    this.subscriptions.set(workspaceId, unsub);
  }

  private markLocal(wsName: string, path: string) {
    const key = `${wsName}:${path}`;
    this.localWritePaths.add(key);
    setTimeout(() => this.localWritePaths.delete(key), 1500);
  }

  async createFile(wsPath: string, file: File): Promise<void> {
    await this.mountPromise;
    const parsed = WsPath.fromString(wsPath);
    const path = relativePathFromWsPath(wsPath);
    const workspaceId = await this.getWorkspaceUuid(parsed.wsName);
    await this.ensureRealtime(parsed.wsName, workspaceId);

    const existing = await getWorkspaceFile(workspaceId, path);
    if (existing) {
      throwAppError('error::file:already-existing', 'File already exists', {
        wsPath,
      });
    }

    const body = await fileToContent(file);
    let storagePath = body.storage_path;
    if (body.content === null) {
      storagePath = await uploadAsset(workspaceId, path, file);
    }

    this.markLocal(parsed.wsName, path);
    const inserted = await insertWorkspaceFile({
      workspace_id: workspaceId,
      path,
      content: body.content,
      storage_path: storagePath,
      content_type: body.content_type,
      byte_size: body.byte_size,
    });
    // Await remote purge only when recreating a name that still has orphan
    // ydoc (fast-path no-ops for fresh names). Local clear is always sync.
    await clearLocalFileYdoc(workspaceId, path);
    await deleteFileYdoc(workspaceId, path);
    this.setListedMeta(workspaceId, path, {
      ctime: new Date(inserted.ctime).getTime(),
      mtime: new Date(inserted.mtime).getTime(),
      contentStat: this.contentStatFromRow(inserted),
    });
    await cacheWorkspaceFile({
      workspaceId,
      path,
      content: inserted.content,
      storagePath: inserted.storage_path,
      contentType: inserted.content_type,
      byteSize: inserted.byte_size,
      mtime: inserted.mtime,
    });

    this.emitChange({ type: 'create', wsPath });
  }

  async deleteFile(wsPath: string): Promise<void> {
    await this.mountPromise;
    const parsed = WsPath.fromString(wsPath);
    const path = relativePathFromWsPath(wsPath);
    const workspaceId = await this.getWorkspaceUuid(parsed.wsName);
    this.markLocal(parsed.wsName, path);
    await deleteWorkspaceFile(workspaceId, path);
    this.workspaceMeta(workspaceId).delete(path);
    await deleteCachedWorkspaceFile(workspaceId, path);
    // Local clear must finish before UI continues; remote purge can finish in
    // the background — abandon-without-persist already stops teardown rewrite,
    // and createFile re-checks orphans if the same name is reused quickly.
    await clearLocalFileYdoc(workspaceId, path);
    void deleteFileYdoc(workspaceId, path);
    this.emitChange({ type: 'delete', wsPath });
  }

  async fileExists(wsPath: string): Promise<boolean> {
    await this.mountPromise;
    const parsed = WsPath.fromString(wsPath);
    const path = relativePathFromWsPath(wsPath);
    const workspaceId = await this.getWorkspaceUuid(parsed.wsName);
    const row = await getWorkspaceFile(workspaceId, path);
    return Boolean(row);
  }

  async fileStat(wsPath: string) {
    await this.mountPromise;
    const parsed = WsPath.fromString(wsPath);
    const path = relativePathFromWsPath(wsPath);
    const workspaceId = await this.getWorkspaceUuid(parsed.wsName);
    const cached = this.workspaceMeta(workspaceId).get(path);
    if (cached) {
      return { ctime: cached.ctime, mtime: cached.mtime };
    }
    const row = await getWorkspaceFile(workspaceId, path);
    if (!row) {
      throwAppError(
        'error::file-storage:file-does-not-exist',
        'File does not exist',
        { wsPath, storage: this.name },
      );
    }
    this.setListedMeta(workspaceId, path, {
      ctime: new Date(row.ctime).getTime(),
      mtime: new Date(row.mtime).getTime(),
      contentStat: this.contentStatFromRow(row),
    });
    return {
      ctime: new Date(row.ctime).getTime(),
      mtime: new Date(row.mtime).getTime(),
    };
  }

  async listAllFiles(
    wsName: string,
    abortSignal: AbortSignal,
  ): Promise<string[]> {
    await this.mountPromise;
    const workspaceId = await this.getWorkspaceUuid(wsName);
    this.wsNameToId.set(wsName, workspaceId);
    await this.ensureRealtime(wsName, workspaceId);
    let rows: Awaited<ReturnType<typeof listWorkspaceFiles>>;
    try {
      rows = await listWorkspaceFiles(workspaceId);
    } catch {
      const cached = await listCachedWorkspaceFiles(workspaceId);
      abortSignal.throwIfAborted();
      return cached
        .map((row: { path: string }) => `${wsName}:${row.path}`)
        .sort((a: string, b: string) => a.localeCompare(b));
    }

    // Paint the tree immediately. Stats backfill is N+1 against PB and used
    // to block listAllFiles for migrated workspaces — run it in the background
    // and refresh once so sidebar word counts catch up.
    const needsBackfill = rows.some(
      (row) =>
        row.word_count == null ||
        row.char_count == null ||
        row.char_count_no_spaces == null,
    );

    const meta = new Map<string, WorkspaceFileMeta>();
    for (const row of rows) {
      meta.set(row.path, {
        ctime: new Date(row.ctime).getTime(),
        mtime: new Date(row.mtime).getTime(),
        contentStat: this.contentStatFromRow(row),
      });
    }
    this.listedMetaByWorkspace.set(workspaceId, meta);

    if (needsBackfill && !this.contentStatsBackfillStarted.has(workspaceId)) {
      this.contentStatsBackfillStarted.add(workspaceId);
      void backfillMissingFileContentStats(workspaceId, rows)
        .then((contentStatsByPath) => {
          for (const [path, stats] of contentStatsByPath) {
            const existing = this.workspaceMeta(workspaceId).get(path);
            if (!existing) continue;
            this.setListedMeta(workspaceId, path, {
              ...existing,
              contentStat: {
                words: stats.wordCount,
                chars: stats.charCount,
                charsNoSpaces: stats.charCountNoSpaces,
              },
            });
          }
          this.onExternalChange?.({ type: 'refresh', wsName });
        })
        .catch(() => {
          // Allow a later list to retry if this attempt failed entirely.
          this.contentStatsBackfillStarted.delete(workspaceId);
        });
    }

    abortSignal.throwIfAborted();
    return rows
      .map((row) => `${wsName}:${row.path}`)
      .sort((a, b) => a.localeCompare(b));
  }

  async readFile(wsPath: string): Promise<File | undefined> {
    await this.mountPromise;
    const parsed = WsPath.fromString(wsPath);
    const path = relativePathFromWsPath(wsPath);
    const workspaceId = await this.getWorkspaceUuid(parsed.wsName);
    const row = await getWorkspaceFile(workspaceId, path);
    if (!row) {
      const cached = await getCachedWorkspaceFile(workspaceId, path);
      if (!cached) return undefined;
      return new File(
        [cached.content ?? ''],
        path.split('/').pop() || 'note.md',
        {
          type: cached.contentType || 'text/markdown',
          lastModified: new Date(cached.mtime).getTime(),
        },
      );
    }
    await cacheWorkspaceFile({
      workspaceId,
      path,
      content: row.content,
      storagePath: row.storage_path,
      contentType: row.content_type,
      byteSize: row.byte_size,
      mtime: row.mtime,
    });

    const name = path.split('/').pop() || 'note.md';
    // Prefer the asset blob whenever storage_path is set. PocketBase may still
    // return content:"" for binary rows; that must not win over the download.
    if (row.storage_path) {
      const blob = await downloadAsset(row.storage_path);
      return new File([blob], name, {
        type: row.content_type || blob.type || 'application/octet-stream',
        lastModified: new Date(row.mtime).getTime(),
      });
    }
    if (row.content != null) {
      return new File([row.content], name, {
        type: row.content_type || 'text/markdown',
        lastModified: new Date(row.mtime).getTime(),
      });
    }
    return new File([''], name, { type: 'text/markdown' });
  }

  async renameFile(
    wsPath: string,
    { newWsPath }: { newWsPath: string },
  ): Promise<void> {
    await this.mountPromise;
    assertSameWorkspaceRename(wsPath, newWsPath);
    const parsed = WsPath.fromString(wsPath);
    const oldPath = relativePathFromWsPath(wsPath);
    const newPath = relativePathFromWsPath(newWsPath);
    const workspaceId = await this.getWorkspaceUuid(parsed.wsName);

    const clash = await getWorkspaceFile(workspaceId, newPath);
    if (clash) {
      throwAppError('error::file:already-existing', 'File already exists', {
        wsPath: newWsPath,
      });
    }

    this.markLocal(parsed.wsName, oldPath);
    this.markLocal(parsed.wsName, newPath);
    const renamed = await renameWorkspaceFile(workspaceId, oldPath, newPath);
    const previous = this.workspaceMeta(workspaceId).get(oldPath);
    this.workspaceMeta(workspaceId).delete(oldPath);
    this.setListedMeta(workspaceId, newPath, {
      ctime: previous?.ctime ?? new Date(renamed.ctime).getTime(),
      mtime: new Date(renamed.mtime).getTime(),
      contentStat: previous?.contentStat ?? this.contentStatFromRow(renamed),
    });
    await renameCachedWorkspaceFile(workspaceId, oldPath, newPath);
    await renameFileYdoc(workspaceId, oldPath, newPath);
    // Drop local caches at both paths so the provider loads the rekeyed remote
    // ydoc (or reseeds from Markdown) instead of a stale IndexedDB entry.
    await clearLocalFileYdoc(workspaceId, oldPath);
    await clearLocalFileYdoc(workspaceId, newPath);
    this.emitChange({
      type: 'rename',
      oldWsPath: wsPath,
      newWsPath,
    });
  }

  async writeFile(wsPath: string, file: File): Promise<void> {
    await this.mountPromise;
    const parsed = WsPath.fromString(wsPath);
    const path = relativePathFromWsPath(wsPath);
    const workspaceId = await this.getWorkspaceUuid(parsed.wsName);

    const body = await fileToContent(file);
    // Binary uploads resolve a fresh asset path up front; markdown passes
    // null so the server preserves the stored path.
    let storagePath: string | null = null;
    if (body.content === null) {
      storagePath = await uploadAsset(workspaceId, path, file);
    }

    this.markLocal(parsed.wsName, path);
    const updated = await this.saveDurableContent(
      workspaceId,
      path,
      body,
      storagePath,
      wsPath,
    );
    this.setListedMeta(workspaceId, path, {
      ctime: new Date(updated.ctime).getTime(),
      mtime: new Date(updated.mtime).getTime(),
      contentStat: this.contentStatFromRow(updated),
    });
    await cacheWorkspaceFile({
      workspaceId,
      path,
      content: updated.content,
      storagePath: updated.storage_path,
      contentType: updated.content_type,
      byteSize: updated.byte_size,
      mtime: updated.mtime,
    });

    this.emitChange({ type: 'update', wsPath });
  }

  private async saveDurableContent(
    workspaceId: string,
    path: string,
    body: {
      content: string | null;
      storage_path: string | null;
      content_type: string;
      byte_size: number;
    },
    storagePath: string | null,
    wsPath: string,
  ) {
    try {
      const row = await saveWorkspaceFileContent({
        workspaceId,
        path,
        content: body.content,
        storagePath,
        contentType: body.content_type,
        byteSize: body.byte_size,
      });
      if (!row) {
        throwAppError(
          'error::file-storage:file-does-not-exist',
          'Cannot write file as it does not exist',
          { wsPath, storage: this.name },
        );
      }
      return row;
    } catch (error) {
      if (!(error instanceof SaveWorkspaceRpcMissingError)) throw error;
    }
    // Legacy path for servers without the save_workspace_file RPC: the
    // previous read + update + share-refresh sequence, request for request.
    const existing = await getWorkspaceFile(workspaceId, path);
    if (!existing) {
      throwAppError(
        'error::file-storage:file-does-not-exist',
        'Cannot write file as it does not exist',
        { wsPath, storage: this.name },
      );
    }
    const updated = await updateWorkspaceFileContent(workspaceId, path, {
      content: body.content,
      storage_path: body.content === null ? storagePath : existing.storage_path,
      content_type: body.content_type,
      byte_size: body.byte_size,
    });
    // Keep public reader caches / asset allowlists in sync after durable writes.
    if (body.content != null) {
      void this.refreshShareLinkCaches(workspaceId, path);
    }
    return updated;
  }

  private async refreshShareLinkCaches(
    workspaceId: string,
    path: string,
  ): Promise<void> {
    try {
      const links = await listShareLinks(workspaceId);
      await Promise.all(
        links
          .filter((link) => link.item_path === path)
          .map((link) => refreshShareLink(link.id)),
      );
    } catch {
      // Owner-only RPC; editors may not refresh — ignore.
    }
  }
}
